import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  IconButton,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  createOrder,
  fetchCustomers,
  fetchCaptains,
  fetchDeliveries,
} from "../../../../../../../services/pos/Tables";

const CreateOrderForm = ({ tableId, rootId, onSuccess }) => {
  const [formData, setFormData] = useState({
    order_type: "takeaway",
    customer_id: "",
    customer_name: "",
    email: "",
    customer_phones: [""],
    customer_addresses: [""],
    captain_order_id: "",
    delivery_id: "",
  });

  const [customers, setCustomers] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const formRef = useRef(null);

  // قائمة خيارات الأرقام المأخوذة من العملاء
  const phoneOptions = customers.flatMap((c) =>
    ["phone1", "phone2", "phone3"]
      .map((k) => ({ phone: c[k], customer: c }))
      .filter((o) => o.phone)
  );

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await fetchCustomers();
        setCustomers(data || []);
      } catch (err) {
        console.error("فشل تحميل العملاء", err);
      }
    };
    loadCustomers();
  }, []);

  useEffect(() => {
    const loadCaptains = async () => {
      try {
        const data = await fetchCaptains();
        setCaptains(data || []);
      } catch (err) {
        console.error("فشل تحميل الكباتن", err);
      }
    };
    loadCaptains();
  }, []);

  useEffect(() => {
    const loadDeliveries = async () => {
      try {
        const data = await fetchDeliveries();
        setDeliveries(data || []);
      } catch (err) {
        console.error("فشل تحميل الدليفري", err);
      }
    };
    loadDeliveries();
  }, []);

  // ✅ useEffect لإضافة اختصار Enter لتنفيذ الإرسال
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من مفتاح Enter فقط
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
        // تجنب التنفيذ إذا كنا داخل TextField متعدد الأسطر
        if (e.target.tagName !== 'TEXTAREA') {
          e.preventDefault(); // منع السلوك الافتراضي
          if (formRef.current) {
            formRef.current.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true })
            );
          }
        }
      }
    };

    // إضافة المستمع
    window.addEventListener('keydown', handleKeyDown);

    // تنظيف المستمع عند unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (idx, value) => {
    const phones = [...formData.customer_phones];
    phones[idx] = value;
    setFormData((prev) => ({ ...prev, customer_phones: phones }));
  };

  const handleAddPhone = () => {
    setFormData((prev) => ({ ...prev, customer_phones: [...prev.customer_phones, ""] }));
  };

  const handleRemovePhone = (idx) => {
    setFormData((prev) => ({ ...prev, customer_phones: prev.customer_phones.filter((_, i) => i !== idx) }));
  };

  const handleAddressChange = (idx, value) => {
    const addresses = [...formData.customer_addresses];
    addresses[idx] = value;
    setFormData((prev) => ({ ...prev, customer_addresses: addresses }));
  };

  const handleAddAddress = () => {
    setFormData((prev) => ({ ...prev, customer_addresses: [...prev.customer_addresses, ""] }));
  };

  const handleRemoveAddress = (idx) => {
    setFormData((prev) => ({ ...prev, customer_addresses: prev.customer_addresses.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const phones = formData.customer_phones;
      const addresses = formData.customer_addresses;
      const payload = {
        ...formData,
        customer_name: formData.customer_name && formData.customer_name.trim() !== "" ? formData.customer_name : "TakeAwayCustomer", // إذا لم يُكتب اسم عميل
        customer_phone1: phones[0] || "",
        customer_phone2: phones[1] || "",
        customer_phone3: phones[2] || "",
        customer_address1: addresses[0] || "",
        customer_address2: addresses[1] || "",
        customer_address3: addresses[2] || "",
        order_type: "delivery",
      };
      delete payload.customer_phones;
      delete payload.customer_addresses;
      delete payload.table_id;

      const res = await createOrder(payload);
      setResult({ success: true, data: res });

      if (onSuccess && res?.data?.order_id) {
        onSuccess({ id: res.data.order_id, status: res.data.sttus });
      }
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={4} sx={{ p: 3, maxWidth: 700, mx: "auto", mt: 4, borderRadius: 3 }}>
      <Typography variant="h5" fontWeight="bold" mb={3} align="center">إنشاء طلب جديد</Typography>
      <form ref={formRef} onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* خانة اسم العميل كتابة وتكملة تلقائية مع إمكانية إضافة اسم جديد */}
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={customers
                .filter(
                  (c) =>
                    c.name?.trim().toLowerCase() !== "tablescustomer" &&
                    c.name?.trim().toLowerCase() !== "deliverycustomer"
                )
                .map(c => c.name)
                .filter(Boolean)
              }
              value={formData.customer_name || ""}
              onChange={(e, newValue) => {
                // إذا كتب اسم جديد أو اختار من القائمة
                setFormData((prev) => {
                  // تحقق إذا الاسم موجود في العملاء
                  const found = customers.find(c => c.name === newValue);
                  if (found) {
                    return {
                      ...prev,
                      customer_name: found.name,
                      customer_id: found.id?.toString() || "",
                      email: found.email || "",
                      customer_phones: [
                        found.phone1 || "",
                        ...(found.phone2 ? [found.phone2] : []),
                        ...(found.phone3 ? [found.phone3] : []),
                      ].filter(Boolean).length
                        ? [
                            found.phone1 || "",
                            ...(found.phone2 ? [found.phone2] : []),
                            ...(found.phone3 ? [found.phone3] : []),
                          ].filter(Boolean)
                        : [""],
                      customer_addresses: [
                        found.address1 || "",
                        found.address2 || "",
                        found.address3 || "",
                      ].filter(Boolean).length
                        ? [
                            found.address1 || "",
                            found.address2 || "",
                            found.address3 || "",
                          ].filter(Boolean)
                        : [""],
                    };
                  } else {
                    // اسم جديد
                    return {
                      ...prev,
                      customer_name: newValue || "",
                      customer_id: "",
                    };
                  }
                });
              }}
              onInputChange={(e, value) =>
                setFormData((prev) => ({ ...prev, customer_name: value }))
              }
              renderInput={(params) => (
                <TextField {...params} label="اسم العميل" variant="outlined" fullWidth />
              )}
            />
          </Grid>

          {/* الهواتف (Autocomplete مع اقتراحات الأرقام) */}
          {formData.customer_phones.map((phone, idx) => (
            <Grid item xs={6} sm={6} key={`phone-${idx}`}>
              <Autocomplete
                freeSolo
                options={phoneOptions}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.phone || "")}
                value={formData.customer_phones[idx] || ""}
                onChange={(e, newValue) => {
                  const phoneVal = typeof newValue === "string" ? newValue : newValue?.phone || "";
                  handlePhoneChange(idx, phoneVal);
                  if (newValue && newValue.customer) {
                    const c = newValue.customer;
                    setFormData((prev) => {
                      const phones = [...prev.customer_phones];
                      phones[idx] = phoneVal;
                      return {
                        ...prev,
                        customer_id: c.id ? c.id.toString() : "",
                        customer_name: c.name || prev.customer_name,
                        email: c.email || prev.email,
                        customer_phones: phones,
                        customer_addresses: [c.address1 || prev.customer_addresses[0], prev.customer_addresses[1], prev.customer_addresses[2]],
                      };
                    });
                  }
                }}
                onInputChange={(e, value) => handlePhoneChange(idx, value)}
                renderInput={(params) => <TextField {...params} label={`هاتف ${idx + 1}`} fullWidth />}
              />
            </Grid>
          ))}
          <Grid item xs={6} sm={6}>
            <Button onClick={handleAddPhone} startIcon={<AddCircleOutlineIcon />} size="small">رقم جديد</Button>
          </Grid>

          {/* العناوين */}
          {formData.customer_addresses.map((address, idx) => (
            <Grid item xs={6} sm={6} key={`address-${idx}`}>
              <TextField
                label={`عنوان ${idx + 1}`}
                value={address}
                onChange={(e) => handleAddressChange(idx, e.target.value)}
                fullWidth
              />
            </Grid>
          ))}
          <Grid item xs={6} sm={6}>
            <Button onClick={handleAddAddress} startIcon={<AddCircleOutlineIcon />} size="small">عنوان جديد</Button>
          </Grid>

          {/* الكابتن */}
          

          {/* الدليفري */}
         

          <Grid item xs={6} sm={6}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              title="إرسال (Enter)"
              endIcon={loading && <CircularProgress size={20} />}
              fullWidth
            >
              {loading ? "جارٍ الإرسال..." : "إرسال"}
            </Button>
          </Grid>
        </Grid>
      </form>

      {result && (
        <Box mt={3}>
          {result.success ? (
            <Alert severity="success">✅ تم إنشاء الطلب بنجاح</Alert>
          ) : (
            <Alert severity="error">❌ {result.message}</Alert>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CreateOrderForm;